/**
 * User Sessions API Client — authenticated account-scoped reads.
 *
 * Talks to the backend endpoints under `/api/user/sessions/*`, each of
 * which is guarded by `authRequired` and scopes every DB query by
 * `user_id = req.user.id`. Identity is JWT-derived — nothing in this
 * client sends a userId over the wire, ever.
 *
 * Used by the authenticated `/history` page. Do NOT reuse this for guest
 * flows: guests do not have JWTs and would be rejected 401.
 */

import { authClient } from '../auth/authClient.js';
import { applyFromSession } from '../chargerUx/canonicalStore';

// A relative path — `authClient` already carries the VITE_API_URL
// baseURL, so this must NOT re-include it. Previously we double-
// prefixed VITE_API_URL here, which produced `/api/api/user/sessions`
// under the dev proxy (VITE_API_URL='/api') and 404'd every history
// load. Every other API client in this repo uses the same bare-path
// convention (`/auth/me`, `/vehicles/mine`, `/stream/tickets`).
const USER_SESSIONS_BASE = '/user/sessions';

// The UUID shape backend `session_uid` uses. We mirror the validator
// so a malformed URL is rejected client-side before it hits the auth
// header + refresh interceptor.
const SESSION_UID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function correlationId() {
  return `ev-user-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Fetch the caller's charging-history page from the account API.
 *
 * @param {{ limit?: number, offset?: number }} [opts]
 * @returns {Promise<
 *   | { ok: true, sessions: object[], total: number, limit: number, offset: number, correlationId: string }
 *   | { ok: false, status?: number, error: string, message: string, correlationId: string }
 * >}
 */
/**
 * Fetch a single owned session's DTO from the account API.
 *
 * Wraps `GET /api/user/sessions/:sessionUid`. The endpoint is
 * `authRequired` and returns 404 for either "no such session" or
 * "not yours" — the client cannot distinguish, so we surface both
 * as `{ ok: false, notFound: true }` and let the caller render the
 * same "we couldn't find this session" copy.
 *
 * @param {string} sessionUid — UUID from the /history/:sessionUid URL.
 * @returns {Promise<
 *   | { ok: true, session: object, correlationId: string }
 *   | { ok: false, notFound: boolean, status?: number, error: string, message: string, correlationId: string }
 * >}
 */
export async function getUserSession(sessionUid) {
  const cid = correlationId();
  if (typeof sessionUid !== 'string' || !SESSION_UID_RE.test(sessionUid)) {
    return {
      ok: false,
      notFound: true,
      status: 400,
      error: 'INVALID_SESSION_UID',
      message: 'That session id is not valid.',
      correlationId: cid,
    };
  }
  try {
    const res = await authClient.get(
      `${USER_SESSIONS_BASE}/${encodeURIComponent(sessionUid)}`,
      { headers: { 'X-Correlation-Id': cid } },
    );
    const body = res.data || {};
    // Charger UX: a session read always feeds the snapshot store (newer wins).
    applyFromSession(body.session);
    return {
      ok: true,
      session: body.session ?? null,
      correlationId: cid,
    };
  } catch (err) {
    const status = err?.response?.status;
    const data = err?.response?.data || {};
    return {
      ok: false,
      status,
      notFound: status === 404,
      error: data.error || 'DETAIL_LOOKUP_FAILED',
      message: data.message || err?.message || 'Could not load this session.',
      correlationId: cid,
    };
  }
}

export async function listUserSessions({ limit = 25, offset = 0 } = {}) {
  const cid = correlationId();
  try {
    const res = await authClient.get(USER_SESSIONS_BASE, {
      params: { limit, offset },
      headers: { 'X-Correlation-Id': cid },
    });
    const body = res.data || {};
    return {
      ok: true,
      sessions: Array.isArray(body.sessions) ? body.sessions : [],
      total: typeof body.total === 'number' ? body.total : 0,
      limit: typeof body.limit === 'number' ? body.limit : limit,
      offset: typeof body.offset === 'number' ? body.offset : offset,
      correlationId: cid,
    };
  } catch (err) {
    const status = err?.response?.status;
    const data = err?.response?.data || {};
    return {
      ok: false,
      status,
      error: data.error || 'HISTORY_LOOKUP_FAILED',
      message: data.message || err?.message || 'Could not load your charging history.',
      correlationId: cid,
    };
  }
}
